import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
    ModalBody, VStack, FormControl, FormLabel, Select, Button,
    RadioGroup, Radio, Stack, useToast, Input, Box, Text
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import api from '../../../auth/apiClient';
import MonitoringInspeksiHarian from "./MonitoringInspeksiHarian";
import MonitoringInspeksiMAMBHarian from "./MonitoringInspeksiMAMBHarian";
import MutuBuah from "./MutuBuah";
import SupervisiPinaltiAncakPanen from "./SupervisiPinaltiAncakPanen";
import InspeksiDenganGambar from "./InspeksiDenganGambar";
import ReportSpvMb from "./ReportSpvMb";
import ReportMtAncakMtBuah from "./ReportMtAncakMtBuah";

interface Reports {
    isOpen: boolean;
    onClose: () => void;
    initialReportType?: 'inspeksi' | 'supervisi' | 'mutu buah' | 'inspeksi gambar' | 'supervisi-mutu buah' | 'inspeksi ma-mb';
}

interface Estate {
    id: number;
    name: string;
}

interface Divisi {
    id: number;
    name: string;
    estate_id: number;
}

const Reports: React.FC<Reports> = ({
    isOpen,
    onClose,
    initialReportType = 'inspeksi' // Default value
}) => {
    const [estates, setEstates] = useState<Estate[]>([]);
    const [divisis, setDivisis] = useState<Divisi[]>([]);
    const [reportType, setReportType] = useState<'inspeksi' | 'supervisi' | 'mutu buah' | 'inspeksi gambar' | 'supervisi-mutu buah' | 'mutu-ancak-mutu-buah' | 'inspeksi ma-mb'>(initialReportType);
    const [selectedMonth, setSelectedMonth] = useState<string>("");
    const [selectedEstate, setSelectedEstate] = useState<number | null>(null);
    const [selectedDivisi, setSelectedDivisi] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // state variables for inspeksi gambar report type
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [selectedUser, setSelectedUser] = useState<string>("");
    const [userSearchQuery, setUserSearchQuery] = useState<string>("");
    const [showUserSuggestions, setShowUserSuggestions] = useState<boolean>(false);
    const [categories, setCategories] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);

    const toast = useToast();

    // Filter users based on search query
    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(userSearchQuery.toLowerCase())
    );

    // Handle user search input
    const handleUserSearchChange = (value: string) => {
        setUserSearchQuery(value);
        setShowUserSuggestions(value.length > 0);
    };

    // Handle user selection
    const handleUserSelect = (user: any) => {
        setSelectedUser(user.username);
        setUserSearchQuery(user.name);
        setShowUserSuggestions(false);
    };

    // Clear user selection
    const clearUserSelection = () => {
        setSelectedUser("");
        setUserSearchQuery("");
        setShowUserSuggestions(false);
    };

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            setShowUserSuggestions(false);
        };

        if (showUserSuggestions) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showUserSuggestions]);

    const months = Array.from({ length: 12 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        return {
            value: `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`,
            label: `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`
        };
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch options from the new endpoint
                const optionsResponse = await api.get('/inspeksi/options');
                const options = optionsResponse.data;

                setEstates(options.estates);
                setDivisis(options.divisis || []);
                setCategories(options.categories);
                setUsers(options.users.map((username: string) => ({ username, name: username })));
            } catch (error) {
                console.error('Error fetching data:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to fetch data',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        };

        fetchData();
    }, [toast]);

    // Handle export berdasarkan jenis laporan
    const handleExport = async () => {
        if (reportType === 'inspeksi gambar') {
            if (!startDate || !endDate || !selectedCategory) {
                toast({
                    title: 'Error',
                    description: 'Tanggal mulai, tanggal akhir, dan kategori harus dipilih',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }
        } else if (!selectedMonth) {
            toast({
                title: 'Error',
                description: 'Bulan harus dipilih',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);

        try {
            if (reportType === 'inspeksi gambar') {
                await InspeksiDenganGambar.export({
                    startDate,
                    endDate,
                    selectedEstate,
                    selectedDivisi,
                    selectedCategory,
                    selectedUser,
                    estates,
                    divisis,
                    setIsLoading,
                    toast
                });
            } else if (reportType === 'inspeksi') {
                await MonitoringInspeksiHarian.export({
                    selectedMonth,
                    selectedEstate,
                    setIsLoading,
                    toast
                });
            } else if (reportType === 'inspeksi ma-mb') {
                await MonitoringInspeksiMAMBHarian.export({
                    selectedMonth,
                    selectedEstate,
                    setIsLoading,
                    toast
                });
            } else if (reportType === 'mutu buah') {
                await MutuBuah.export({
                    selectedMonth,
                    selectedEstate,
                    setIsLoading,
                    months,
                    toast
                });
            } else if (reportType === 'supervisi-mutu buah') {
                await ReportSpvMb.export({
                    selectedMonth,
                    selectedEstate,
                    setIsLoading,
                    months,
                    toast
                });
            } else if (reportType === 'mutu-ancak-mutu-buah') {
                await ReportMtAncakMtBuah.export({
                    selectedMonth,
                    selectedEstate,
                    setIsLoading,
                    months,
                    toast
                });
            } else {
                await SupervisiPinaltiAncakPanen.export({
                    selectedMonth,
                    selectedEstate,
                    setIsLoading,
                    months,
                    toast
                });
            }
        } catch (error) {
            console.error('Export error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Reset state saat modal ditutup
    useEffect(() => {
        if (!isOpen) {
            setReportType(initialReportType);
            setSelectedMonth("");
            setSelectedEstate(null);
            setSelectedDivisi(null);
            setStartDate("");
            setEndDate("");
            setSelectedCategory("");
            setSelectedUser("");
            setUserSearchQuery("");
            setShowUserSuggestions(false);
        }
    }, [isOpen, initialReportType]);

    // Helper functions untuk UI text
    const getLoadingText = () => {
        switch (reportType) {
            case 'inspeksi':
                return "Mengekspor Inspeksi...";
            case 'inspeksi ma-mb':
                return "Mengekspor Inspeksi...";
            case 'supervisi':
                return "Mengekspor Supervisi...";
            case 'mutu buah':
                return "Mengekspor Mutu Buah...";
            case 'supervisi-mutu buah':
                return "Mengekspor Supervisi-Mutu Buah...";
            case 'mutu-ancak-mutu-buah':
                return "Mengekspor Mutu-Ancak-Mutu-Buah...";
            case 'inspeksi gambar':
                return "Mengekspor Inspeksi dengan Gambar...";
            default:
                return "Mengekspor...";
        }
    };

    const getButtonText = () => {
        switch (reportType) {
            case 'inspeksi':
                return 'Unduh Laporan Inspeksi';
            case 'inspeksi ma-mb':
                return 'Unduh Laporan Inspeksi';
            case 'supervisi':
                return 'Unduh Laporan Supervisi';
            case 'mutu buah':
                return 'Unduh Laporan Mutu Buah';
            case 'supervisi-mutu buah':
                return 'Unduh Laporan Supervisi-Mutu Buah';
            case 'mutu-ancak-mutu-buah':
                return "Unduh Laporan Mutu-Ancak-Mutu-Buah...";
            case 'inspeksi gambar':
                return 'Unduh Laporan Inspeksi dengan Gambar';
            default:
                return 'Unduh Laporan';
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Unduh Laporan</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <VStack spacing={4}>
                        {/* Pilihan Jenis Laporan */}
                        <FormControl isRequired>
                            <FormLabel>Jenis Laporan</FormLabel>
                            <RadioGroup
                                value={reportType}
                                onChange={(value) => setReportType(value as 'inspeksi' | 'supervisi' | 'mutu buah' | 'inspeksi gambar' | 'supervisi-mutu buah')}
                            >
                                <Stack
                                    direction={{ base: "column", md: "row" }}
                                    spacing={4}
                                    align="start"
                                    wrap="wrap"
                                >
                                    <Radio value="inspeksi">Monitoring Inspeksi Harian</Radio>
                                    <Radio value="inspeksi ma-mb">Monitoring Inspeksi MA-MB Harian</Radio>
                                    <Radio value="supervisi">Supervisi Pinalti Ancak Panen</Radio>
                                    <Radio value="mutu buah">Mutu Buah</Radio>
                                    <Radio value="supervisi-mutu buah">Supervisi-Mutu Buah</Radio>
                                    <Radio value="mutu-ancak-mutu-buah">Mutu Ancak - Mutu Buah</Radio>
                                    <Radio value="inspeksi gambar">Inspeksi Dengan Gambar</Radio>
                                </Stack>
                            </RadioGroup>
                        </FormControl>

                        {/* Conditional filters based on report type */}
                        {reportType === 'inspeksi gambar' ? (
                            <>
                                {/* Date Range for Inspeksi Gambar */}
                                <FormControl isRequired>
                                    <FormLabel>Tanggal Mulai</FormLabel>
                                    <Input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>Tanggal Akhir</FormLabel>
                                    <Input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </FormControl>

                                {/* Category - Required */}
                                <FormControl isRequired>
                                    <FormLabel>Kategori</FormLabel>
                                    <Select
                                        placeholder="Pilih kategori"
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        {categories.map((category) => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Estate - Optional */}
                                <FormControl>
                                    <FormLabel>Estate (Opsional)</FormLabel>
                                    <Select
                                        placeholder="Semua Estate"
                                        value={selectedEstate ?? ''}
                                        onChange={(e) => {
                                            const estateVal = e.target.value ? Number(e.target.value) : null;
                                            setSelectedEstate(estateVal);
                                            setSelectedDivisi(null);
                                        }}
                                    >
                                        {estates.map((estate) => (
                                            <option key={estate.id} value={estate.id}>
                                                {estate.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Divisi - Optional */}
                                <FormControl>
                                    <FormLabel>Divisi (Opsional)</FormLabel>
                                    <Select
                                        placeholder="Semua Divisi"
                                        value={selectedDivisi ?? ''}
                                        onChange={(e) => setSelectedDivisi(e.target.value ? Number(e.target.value) : null)}
                                        isDisabled={!selectedEstate}
                                    >
                                        {divisis
                                            .filter((divisi) => divisi.estate_id === selectedEstate)
                                            .map((divisi) => (
                                                <option key={divisi.id} value={divisi.id}>
                                                    {divisi.name}
                                                </option>
                                            ))}
                                    </Select>
                                </FormControl>

                                {/* User */}
                                <FormControl>
                                    <FormLabel>User (Opsional)</FormLabel>
                                    <Box position="relative">
                                        <Input
                                            placeholder="Cari user..."
                                            value={userSearchQuery}
                                            onChange={(e) => handleUserSearchChange(e.target.value)}
                                            onFocus={() => setShowUserSuggestions(userSearchQuery.length > 0)}
                                        />
                                        {showUserSuggestions && filteredUsers.length > 0 && (
                                            <Box
                                                position="absolute"
                                                top="100%"
                                                left={0}
                                                right={0}
                                                zIndex={10}
                                                bg="white"
                                                border="1px solid"
                                                borderColor="gray.200"
                                                borderRadius="md"
                                                maxHeight="200px"
                                                overflowY="auto"
                                                boxShadow="md"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {filteredUsers.slice(0, 10).map((user) => (
                                                    <Box
                                                        key={user.username}
                                                        p={2}
                                                        cursor="pointer"
                                                        _hover={{ bg: "gray.100" }}
                                                        onClick={() => handleUserSelect(user)}
                                                    >
                                                        <Text fontSize="sm">{user.name}</Text>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        {selectedUser && (
                                            <Text fontSize="xs" color="gray.600" mt={1}>
                                                Dipilih: {userSearchQuery}
                                                <Button
                                                    size="xs"
                                                    ml={2}
                                                    onClick={clearUserSelection}
                                                    variant="ghost"
                                                >
                                                    ✕
                                                </Button>
                                            </Text>
                                        )}
                                    </Box>
                                </FormControl>
                            </>
                        ) : (
                            <>
                                {/* Bulan for other report types */}
                                <FormControl isRequired>
                                    <FormLabel>Bulan</FormLabel>
                                    <Select
                                        placeholder="Pilih bulan"
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(e.target.value)}
                                    >
                                        {months.map((month) => (
                                            <option key={month.value} value={month.value}>
                                                {month.label}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Estate for other report types */}
                                <FormControl>
                                    <FormLabel>Estate (Opsional)</FormLabel>
                                    <Select
                                        placeholder="Semua Estate"
                                        value={selectedEstate ?? ''}
                                        onChange={(e) => setSelectedEstate(e.target.value ? Number(e.target.value) : null)}
                                    >
                                        {estates.map((estate) => (
                                            <option key={estate.id} value={estate.id}>
                                                {estate.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}

                        <Button
                            colorScheme="green"
                            onClick={handleExport}
                            isLoading={isLoading}
                            loadingText={getLoadingText()}
                            width="full"
                            mt={4}
                        >
                            {getButtonText()}
                        </Button>
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default Reports;